import { Graph, Node } from '@antv/x6';
import { SwitchDevice, PCDevice, DHCPServerDevice, RouterDevice, ModemDevice } from './Simulator/Ethernet';
import { FirewallDevice } from './Simulator/Firewall';
import { debounce } from '../../tools.js';
import { DeviceType, ISimulatorDevice, IHALInterface } from './Simulator/Base';
import { IWIFIDevice, APDevice, MobileDevice, HomeGatewayDevice } from './Simulator/WIFI';
import { SmartBulbDevice, SmartLockDevice, SmartBellDevice, SmartSpeakerDevice, AirConditionerDevice, ProjectorDevice, ScreenDevice } from './Simulator/IOT';
import { BluetoothDevice } from './Simulator/Bluethooth';

// 图形化和网络模拟器桥接器
export class GraphNetworkSimulatorBridge {
    private onChange: Function;
    private graph: Graph;
    private nodeIDDeviceDict: {[nodeID: string]: ISimulatorDevice};
    private portIDInterfaceDict: {[portID: string]: IHALInterface};
    private macInterfaceDict:  {[mac: string]: string};
    private uiConfig: {
      networkTabEnabled: boolean;
      iotTabEnabled: boolean;
      networkDevices: string[];
      iotDevices: string[];
    };

    constructor(graph: Graph, onChange: Function) {
      this.onChange = onChange;
      this.graph = graph;
      this.nodeIDDeviceDict = {};
      this.portIDInterfaceDict = {};
      this.macInterfaceDict = {};
      this.uiConfig = GraphNetworkSimulatorBridge.getDefaultUIConfig();

      // 去抖动变更通知事件
      const onGraphChange = debounce(this.onGraphChange.bind(this), 500, false);

      // 监听修改事件
      graph.on('node:removed', this.onNodeRemoved);
      graph.on('node:changed', onGraphChange);
      graph.on('edge:added', onGraphChange);
      graph.on('edge:removed', this.onEdgeRemoved);
      graph.on('edge:changed', this.onEdgeAdded);
    }

    /**
     * 销毁，退出时调用，解除监听
     */
    destory() {
      const { graph } = this;

      // 解除监听修改事件
      graph.off('node:removed', this.onNodeRemoved);
      graph.off('edge:added', this.onEdgeAdded);
      graph.off('edge:removed', this.onEdgeRemoved);
    }

    addNode(nodeProps: any) {
      const { graph } = this;

      return graph.addNode(nodeProps);
    }

    findView(node: Node) {
      const { graph } = this;

      return graph.findViewByCell(node);
    }

    // 添加对应节点
    onNodeAdded = (node: Node, deviceConfig: any = null, WIFIDevices: Array<IWIFIDevice> | null = null, bluetoothDevices: Array<BluetoothDevice> | null = null) => {
      const { shape } = node;
      let device: ISimulatorDevice | null = null;

      switch(shape) {
        // 以下为以太网设备
        case DeviceType.PC:
          device = new PCDevice(node);
          break;
        case DeviceType.Switch:
          // 构建仿真集线器节点
          device = new SwitchDevice(node, 5);
          break;
        case DeviceType.DHCPServer:
          device = new DHCPServerDevice(node);
          break;
        case DeviceType.Router:
          device = new RouterDevice(node);
          break;
        case DeviceType.Firewall:
          device = new FirewallDevice(node);
          break;
        case DeviceType.Modem:
          device = new ModemDevice(node);
          break;
        // 以下为WIFI设备
        case DeviceType.AP:
          device = new APDevice(node);
          break;
        case DeviceType.MobileDevice:
          device = new MobileDevice(node);

          if(WIFIDevices) {
            WIFIDevices.push(device as MobileDevice);
          }
          break;
        case DeviceType.HomeGateway:
          device = new HomeGatewayDevice(node);
          break;
        // 以下为蓝牙设备
        case DeviceType.SmartSpeaker:
          device = new SmartSpeakerDevice(node);

          if(WIFIDevices) {
            WIFIDevices.push((device as SmartSpeakerDevice).mobileDeviceInstance);
          }
          break;
        case DeviceType.SmartBulb:
          device = new SmartBulbDevice(node);

          if(bluetoothDevices) {
            bluetoothDevices.push(device as SmartBulbDevice);
          }
          break;
        case DeviceType.SmartLock:
          device = new SmartLockDevice(node);

          if(bluetoothDevices) {
            bluetoothDevices.push(device as SmartBulbDevice);
          }
          break;
        case DeviceType.SmartBell:
          device = new SmartBellDevice(node);

          if(bluetoothDevices) {
            bluetoothDevices.push(device as SmartBulbDevice);
          }
          break;
        // 红外设备
        case DeviceType.AirConditioner:
          device = new AirConditionerDevice(node);
          break;
        case DeviceType.Projector:
          device = new ProjectorDevice(node);
          break;
        case DeviceType.Screen:
          device = new ScreenDevice(node);
          break;
        default:
          throw new Error(`未识别的设备类型${shape}`)
      }

      // 加入模拟器环境
      if (device) {
        this.addSimulatorDevice(node, device, deviceConfig);
      }
    }

    // 删除对应节点
    onNodeRemoved = async(e: any) => {
      const { node } = e;
      // console.log('onNodeRemoved', node);

      // 定位到nodeID
      const { id } = node;
      const device = this.nodeIDDeviceDict[id];

      if (!device) {
        this.onGraphChange();
        return;
      }

      const { macInterfaceDict, portIDInterfaceDict, nodeIDDeviceDict } = this;

      // 获取设备端口
      const ports = device.getPorts();
      for(const port of ports) {
        const { mac } = port;
        const portID = macInterfaceDict[mac];

        delete macInterfaceDict[mac];
        delete portIDInterfaceDict[portID];
      }

      // 删除索引
      delete nodeIDDeviceDict[id];

      // 声明图形变化
      this.onGraphChange();
    }

    // 增加连线
    onEdgeAdded = (e: any) => {
      const { edge } = e;

      // 获取端口ID
      const sourcePortID = edge.getSourcePortId();
      const targetPortID = edge.getTargetPortId();

      // 绘制中的不处理
      if(!targetPortID) {
        return;
      }

      // 获取端口
      const sourceInterface = this.portIDInterfaceDict[sourcePortID];
      const targetInterface = this.portIDInterfaceDict[targetPortID];

      // 建立连线
      if(!sourceInterface || !targetInterface) {
        return;
      }
      
      sourceInterface.link(targetInterface);
      targetInterface.link(sourceInterface);
      
      // 声明图形变化
      this.onGraphChange();
    }

    // 删除连线
    onEdgeRemoved = (e: any) => {
      const { edge } = e;

      // 获取端口ID
      const sourcePortID = edge.getSourcePortId();
      const targetPortID = edge.getTargetPortId();

      // 获取端口
      const sourceInterface = this.portIDInterfaceDict[sourcePortID];
      const targetInterface = this.portIDInterfaceDict[targetPortID];

      // 删除连线
      sourceInterface.unlink();

      if(targetInterface) {
        targetInterface.unlink();
      }
      
      // 声明图形变化
      this.onGraphChange();
    }

    // 声明变更，此时并不生成数据，另外提供toJSON接口生成数据
    onGraphChange() {
      const { onChange } = this;
      if(onChange) {
        onChange();
      }

      // console.log(this.toJSON());
    }

    // 获取设备
    getDeviceByNode(node: any): ISimulatorDevice {
      return this.nodeIDDeviceDict[node.id];
    }

    // 有JSON文本恢复
    fromJSON(storageObject: any) {
      // 准备恢复数据
      const {
        graphStorage,
        deviceStorage,
        portStorage,
        uiConfig,
      } = storageObject;

      // 空图像，退出
      if(!graphStorage) {
        return;
      }

      const { graph } = this;

      const nodeIDDeviceDict: {[nodeID: string]: ISimulatorDevice} = {};
      const portIDInterfaceDict: {[portID: string]: IHALInterface} = {};
      const macInterfaceDict:  {[mac: string]: string} = {};

      this.nodeIDDeviceDict = nodeIDDeviceDict;
      this.portIDInterfaceDict = portIDInterfaceDict;
      this.macInterfaceDict = macInterfaceDict;
      this.uiConfig = uiConfig && uiConfig.networkDevices ? uiConfig : GraphNetworkSimulatorBridge.getDefaultUIConfig();

      // 图形部分
      graph.fromJSON(graphStorage);

      // 建立NodeID到Node索引
      const nodeID2Node: {[nodeID: string]: Node} = {};

      graph.getNodes().forEach((node: any) => {
        const { id } = node;
        nodeID2Node[id] = node;
      });

      // 端口部分，建立mac到portID表
      for(const portID in portStorage) {
        const mac = portStorage[portID];
        macInterfaceDict[mac] = portID;
      }

      // 设备部分，移动设备需要单独重连
      const WIFIDevices: Array<IWIFIDevice> = [];

      // 设备部分，蓝牙设备需要单独重连
      const bluetoothDevices: Array<BluetoothDevice> = [];

      for(const nodeID in deviceStorage) {
        const deviceConfig = deviceStorage[nodeID];
        const node = nodeID2Node[nodeID];
       
        this.onNodeAdded(node, deviceConfig, WIFIDevices, bluetoothDevices);
      }

      // 恢复线路连接
      for(const portID in portIDInterfaceDict) {
        const port = portIDInterfaceDict[portID];
        if(port.connected) {
          continue;
        }

        const linkedToMac = port.connectedMac;
        if(!linkedToMac) {
          continue;
        }

        const linkedToPortID = macInterfaceDict[linkedToMac];
        const linkedToPort = portIDInterfaceDict[linkedToPortID];

        port.link(linkedToPort);
        linkedToPort.link(port);
      }

      // 恢复移动设备重连
      for(const WIFIDevice of WIFIDevices) {
        WIFIDevice.restore();
      }

      // 恢复蓝牙设备重连
      for(const bluetoothDevice of bluetoothDevices) {
        bluetoothDevice.restore();
      }
    }

    // 持久化为JSON对象
    toJSON() {
      // 图形部分
      const { graph, nodeIDDeviceDict, portIDInterfaceDict } = this;
      const graphStorage = graph.toJSON();
    
      // 删除数据中的tools属性
      const { cells } = graphStorage;
      cells.forEach(cell =>　{
        if (cell.tools) {
          delete cell.tools
        }
      })
      
      // 设备部分(含端口配置)
      const deviceStorage: {[nodeID: string]: any} = {};
      for(const nodeID in nodeIDDeviceDict) {
        const device = nodeIDDeviceDict[nodeID];
        deviceStorage[nodeID] = device.toJSON();
      }

      // 端口连线部分
      const portStorage:{[portID:string]: string } = {}
      for(const portID in portIDInterfaceDict) {
        const port = portIDInterfaceDict[portID];
        portStorage[portID] = port.mac;
      }

      // 将三个部分合并作为存储格式
      return {
        graphStorage,
        deviceStorage,
        portStorage,
        uiConfig: this.uiConfig,
      };
    }

    static getDefaultUIConfig() {
      return {
        networkTabEnabled: true,
        iotTabEnabled: true,
        networkDevices: ['计算机', '交换机', '无线路由器', 'DHCP服务器', '移动终端', '无线接入点', '调制解调器', '路由器', '防火墙'],
        iotDevices: ['智能音箱', '智能门铃', '智能门锁', '智能灯泡', '投影机', '空调', '投影幕布'],
      };
    }

    setUIConfig(config: {
      networkTabEnabled?: boolean;
      iotTabEnabled?: boolean;
      networkDevices?: string[];
      iotDevices?: string[];
    }) {
      const nextConfig = {
        ...GraphNetworkSimulatorBridge.getDefaultUIConfig(),
        ...this.uiConfig,
        ...config,
      };
      this.uiConfig = nextConfig;
      this.onGraphChange();
    }

    getUIConfig() {
      return this.uiConfig || GraphNetworkSimulatorBridge.getDefaultUIConfig();
    }

    /**
     * 按照设备类型加入
     * @param deviceName 设备类型
     * @param x x坐标
     * @param y y坐标
     */
    addGraphNode(deviceTypeName: string, x: number, y: number) {
      const { graph } = this;

      // 绘制图形的基本节点
      let node = null;
      switch(deviceTypeName) {
        case '计算机':
          node = graph.addNode({
            shape: 'PC',
            x: x - 82 * 2,
            y: y - 72,
          });
          break;
        case '交换机':
          node = graph.addNode({
            shape: 'Switch',
            x: x - 108 * 1.8,
            y: y - 55,
          });
          break;
        case 'DHCP服务器':
          node = graph.addNode({
            shape: 'DHCPServer',
            x: x - 108 * 1.8,
            y: y - 75
          });
          break;
        case '移动终端':
          node = graph.addNode({
            shape: 'MobileDevice',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '无线接入点':
          node = graph.addNode({
            shape: 'AP',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '调制解调器':
          node = graph.addNode({
            shape: 'Modem',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '路由器':
          node = graph.addNode({
            shape: 'Router',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '防火墙':
          node = graph.addNode({
            shape: 'Firewall',
            x: x - 90,
            y: y - 66,
          });
          break;
        case '无线路由器':
          node = graph.addNode({
            shape: 'HomeGateway',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '智能灯泡':
          node = graph.addNode({
            shape: 'SmartBulb',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '空调':
          node = graph.addNode({
            shape: 'AirConditioner',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '智能门锁':
          node = graph.addNode({
            shape: 'SmartLock',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '智能门铃':
          node = graph.addNode({
            shape: 'SmartBell',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '智能音箱':
          node = graph.addNode({
            shape: 'SmartSpeaker',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '投影机':
          node = graph.addNode({
            shape: 'Projector',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        case '投影幕布':
          node = graph.addNode({
            shape: 'Screen',
            x: x - 72 * 2.2,
            y: y - 60,
          });
          break;
        default:
          throw new Error(`未识别的设备类型${deviceTypeName}`)
      }

      // 增加对应设备节点
      this.onNodeAdded(node);

      // 声明图形变化
      this.onGraphChange();
    }

    // 添加仿真设备
    addSimulatorDevice(node: Node, device: ISimulatorDevice, deviceConfig: any = null) {
      const { nodeIDDeviceDict, macInterfaceDict, portIDInterfaceDict } = this;

      // 配置桥接器
      device.setBridge(this);

      // 如遇有配置提供的话，则恢复配置
      if(deviceConfig) {
        device.fromJSON(deviceConfig);
      }

      // 提取交换机节点ID和端口ID数组
      const nodeID = node.id;
      const portIDs = node.getPorts().map(port => port.id as string);

      // 建立节点映射
      nodeIDDeviceDict[nodeID] = device;

      // 获取端口
      const ports = device.getPorts();

      // 建立端口映射
      for(let i = 0; i < portIDs.length; i++) {
        const portID = portIDs[i];
        const port = ports[i];
        const mac = port.mac;
        
        portIDInterfaceDict[portID] = port;
        macInterfaceDict[mac] = portID;
      }

      // 执行设备图形效果或动画
    }
}